import { MaterialCommunityIcons } from "@expo/vector-icons";
import React from "react";
import type {
  OpaqueColorValue,
  PressableProps,
  StyleProp,
  TextStyle,
  ViewStyle,
} from "react-native";
import { Pressable, StyleSheet, Text } from "react-native";

export type ButtonProps = {
  children?: React.ReactNode;
  accessibilityLabel?: string;
  accessibilityState?: PressableProps["accessibilityState"];
  buttonText?: string;
  onPress?: () => void;
  disabled?: boolean;
  buttonStyle?: StyleProp<ViewStyle>;
  buttonTextStyle?: StyleProp<TextStyle>;
  iconColor?: string | OpaqueColorValue;
  iconName?: React.ComponentProps<typeof MaterialCommunityIcons>["name"];
  iconPosition?: "left" | "right";
  iconSize?: number;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  testID?: string;
};

export const Button = ({
  children,
  accessibilityLabel,
  accessibilityState,
  buttonText,
  onPress,
  disabled,
  buttonStyle,
  buttonTextStyle,
  iconColor,
  iconName,
  iconPosition,
  iconSize = 32,
  onHoverIn,
  onHoverOut,
  testID,
}: ButtonProps) => {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      aria-busy={accessibilityState?.busy}
      style={({ pressed }) => [
        styles.button,
        disabled && styles.buttonDisabled,
        buttonStyle,
        pressed && { opacity: 0.2 },
      ]}
      disabled={disabled}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
    >
      {iconName && iconPosition === "left" && (
        <MaterialCommunityIcons
          name={iconName}
          size={iconSize}
          color={iconColor}
        />
      )}
      {buttonText && (
        <Text style={[styles.buttonText, buttonTextStyle]}>{buttonText}</Text>
      )}
      {iconName && iconPosition !== "left" && (
        <MaterialCommunityIcons
          name={iconName}
          size={iconSize}
          color={iconColor}
        />
      )}
      {children}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    display: "flex",
    flexDirection: "row",
    gap: 16,
    height: 80,
    minWidth: 80,
    alignItems: "center",
    borderRadius: 40,
    paddingHorizontal: 32,
  },
  buttonText: {
    fontSize: 20,
    lineHeight: 28,
    flexGrow: 1,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});
